import { ArrowLeft, Zap, User, Weight, Activity, Clock } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";

const BBSDetailPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-20"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 shrink-0 rounded-xl border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1 text-center">
            <p className="text-sm font-medium text-muted-foreground">Health Score</p>
            <h1 className="text-2xl font-semibold text-foreground">Bite Balance Score</h1>
          </div>
          <div className="h-10 w-10 shrink-0" aria-hidden />
        </div>

        {/* What is BBS? Info Card */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-3 text-callout font-semibold text-foreground">
            What is BBS?
          </h3>
          <p className="text-callout leading-relaxed text-muted-foreground">
            The <span className="font-semibold text-blue-600">Bite Balance Score</span> is a single daily metric that combines your meal quality, body composition, physical activity into one easy-to-read number from 0 to 100.
          </p>
        </Card>

        {/* Glycemic Load Penalty */}
        <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-start gap-3">
            <div className="rounded-full bg-amber-100 p-2 dark:bg-amber-900">
              <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h4 className="mb-1 text-callout font-semibold text-foreground">
                Glycemic Load Penalty
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                This factor measures how much your meals may raise blood sugar. It considers net carbs relative to fiber intake, and is moderated by protein and fat — both slow glucose absorption. A higher penalty lowers your score.
              </p>
            </div>
          </div>
        </Card>

        {/* Gender Factor */}
        <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-start gap-3">
            <div className="rounded-full bg-purple-100 p-2 dark:bg-purple-900">
              <User className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h4 className="mb-1 text-callout font-semibold text-foreground">
                Gender Factor
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Biological sex influences how the body processes glucose and stores energy. This factor adjusts your score to account for known metabolic differences between males and females.
              </p>
            </div>
          </div>
        </Card>

        {/* BMI Factor */}
        <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-start gap-3">
            <div className="rounded-full bg-orange-100 p-2 dark:bg-orange-900">
              <Weight className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="flex-1">
              <h4 className="mb-1 text-callout font-semibold text-foreground">
                BMI Factor
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Body mass index reflects body composition and influences insulin sensitivity. A higher BMI amplifies the glycemic load penalty, while a lower BMI reduces it.
              </p>
            </div>
          </div>
        </Card>

        {/* Activity Bonus */}
        <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-start gap-3">
            <div className="rounded-full bg-green-100 p-2 dark:bg-green-900">
              <Activity className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <h4 className="mb-1 text-callout font-semibold text-foreground">
                Activity Bonus
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Physical activity helps your body use glucose more efficiently. This bonus is earned from your daily movement data — including average intensity, peak effort, and overall activity load. More movement means a higher score boost.
              </p>
            </div>
          </div>
        </Card>

        {/* Activity Zone Minutes */}
        <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-start gap-3">
            <div className="rounded-full bg-teal-100 p-2 dark:bg-teal-900">
              <Clock className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div className="flex-1">
              <h4 className="mb-1 text-callout font-semibold text-foreground">
                Activity Zone Minutes
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                When detailed heart-rate data isn't available, your Activity Zone Minutes from your wearable device act as a proxy to estimate exercise intensity and calculate your activity bonus.
              </p>
            </div>
          </div>
        </Card>

        {/* Score Ranges */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-callout font-semibold text-foreground">
            Score Ranges
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-border/40 pb-3">
              <span className="text-callout font-medium text-foreground">
                Excellent
              </span>
              <span className="text-callout font-semibold text-green-600">
                80 – 100
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border/40 pb-3">
              <span className="text-callout font-medium text-foreground">Good</span>
              <span className="text-callout font-semibold text-blue-600">60 – 79</span>
            </div>
            <div className="flex items-center justify-between border-b border-border/40 pb-3">
              <span className="text-callout font-medium text-foreground">Caution</span>
              <span className="text-callout font-semibold text-orange-600">
                40 – 59
              </span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-callout font-medium text-foreground">
                Poor
              </span>
              <span className="text-callout font-semibold text-red-600">0 – 39</span>
            </div>
          </div>
        </Card>
      </div>

      <BottomNavBar activeIndex={0} />
    </div>
  );
};

export default BBSDetailPage;
